/* ───────────────────────────────────────────────────────────────────────────
   TuapAdmin — the TUAP handover + tracking section inside the FME admin.

   Rajib converts an FM lead to TUAP; the student then appears here. For each
   TUAP student this shows: the onboarding link to send, the WhatsApp group,
   live onboarding status, and every onboarding response once submitted.
   ─────────────────────────────────────────────────────────────────────────── */

import { useState, useEffect } from "react";
import {
  subscribeTuapStudents, updateTuapStudent, requestTuapGroup,
  sendOnbEmail, getTuapFiles, askStudent, ONB_DOCUMENTS, TUAP_PIPELINE,
  liveStatus, deleteTuapStudent, groupByGrade,
} from "./tuap";
import StudentPortal from "./StudentPortal";
import Lightbox from "./Lightbox";
import LiveStatus from "./LiveStatus";
import RavishaHub from "./RavishaHub";
import { fetchOtgEssays, essayMatchesStudent, essayNames,
  subscribeOnboardingProfiles, onbLiveForStudent } from "./otgEssays";

// A student's portal URL — clean slug, or the legacy token link.
function portalUrl(s) {
  return s.slug
    ? `${window.location.origin}/${s.slug}`
    : `${window.location.origin}/student?id=${s.id}&t=${s.accessToken}`;
}

const Y = "#F2B100", YD = "#8A6400";
const INK = "#1A1A1E", MID = "#5C5C66", SUB = "#9A9AA2";
const BORDER = "#E6E6E9", BG = "#F6F6F7", CARD = "#FFFFFF";

const FONTS = "@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800;900&family=Space+Mono:wght@400;700&display=swap');";
const FONT = "'Archivo',-apple-system,sans-serif";

const STATUS_COLOR = {
  "Handover": "#6b7280", "Group Created": "#2563eb", "ONB Sent": "#7c3aed",
  "ONB In Progress": "#ca8a04", "ONB Complete": "#0d9488",
  "Profile Setup": "#ea580c", "Active": "#16a34a",
};

function Badge({ text }) {
  const c = STATUS_COLOR[text] || "#6b7280";
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: c, background: c + "1a",
      padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap" }}>{text || "—"}</span>
  );
}

/* ── Student detail ── */
function Detail({ student, onBack, onbProfiles }) {
  const [link, setLink] = useState(student.whatsappLink || "");
  const [copied, setCopied] = useState(false);
  const [sendingOnb, setSendingOnb] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [allFiles, setAllFiles] = useState({});
  const [lb, setLb] = useState(null);
  const [askQ, setAskQ] = useState("");
  const [askRes, setAskRes] = useState(null);
  const [copiedEssay, setCopiedEssay] = useState(false);
  // LIVE new-onboarding status for this student (otg-2026 onboardingProfiles).
  const onbLive = onbLiveForStudent(onbProfiles || [], student);
  const onbUrl = portalUrl(student);
  const essayUrl = student.slug
    ? `${window.location.origin}/essay/${student.slug}`
    : portalUrl(student);
  const groupName = `${student.studentName || "Student"} (NM Squad TUAP)`;

  function copy(text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  }
  async function openFiles() {
    setShowFiles(true);
    try { setAllFiles(await getTuapFiles(student.id)); } catch (e) { /* ignore */ }
  }
  async function sendOnb() {
    setSendingOnb(true);
    try {
      const r = await sendOnbEmail(student);
      alert(r.ok ? `Onboarding email sent to ${student.studentEmail}.`
        : `Could not send: ${r.error || "unknown error"}`);
    } catch (e) { alert("Could not send the email."); }
    setSendingOnb(false);
  }

  const sectionTitle = { margin: "26px 0 10px", fontSize: 12, fontWeight: 700, color: YD,
    fontFamily: "'Space Mono',monospace", textTransform: "uppercase", letterSpacing: ".07em" };
  const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 18 };

  return (
    <div style={{ maxWidth: 1240, margin: "0 auto", padding: "22px 26px 60px" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: SUB,
        fontSize: 13, fontWeight: 700, cursor: "pointer", padding: 0, marginBottom: 14 }}>
        ← All TUAP students
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: INK }}>
          {student.studentName || "Unnamed student"}
        </h1>
        <Badge text={student.status} />
        <span style={{ fontSize: 13, color: SUB, fontWeight: 600 }}>
          {student.grade || "Grade —"}{student.school ? ` · ${student.school}` : ""}
        </span>
      </div>

      {/* quick links */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
        <a href={onbUrl} target="_blank" rel="noreferrer" style={{ background: Y, color: YD,
          fontSize: 13, fontWeight: 800, padding: "9px 16px", borderRadius: 8,
          textDecoration: "none" }}>Open portal</a>
        <button onClick={openFiles} style={{ background: "#eef2ff", color: "#3730a3",
          border: "none", fontSize: 13, fontWeight: 800, padding: "9px 16px", borderRadius: 8,
          cursor: "pointer" }}>Open files</button>
        {student.whatsappLink && (
          <a href={student.whatsappLink} target="_blank" rel="noreferrer" style={{ background: "#25D366",
            color: "#fff", fontSize: 13, fontWeight: 800, padding: "9px 16px", borderRadius: 8,
            textDecoration: "none" }}>Open WhatsApp group</a>
        )}
      </div>

      {/* ask about this student */}
      <div style={sectionTitle}>Ask about this student</div>
      <div style={card}>
        <div style={{ fontSize: 12.5, color: SUB, marginBottom: 8 }}>
          Type a question — answers are pulled straight from the onboarding responses.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input value={askQ} onChange={e => setAskQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && setAskRes(askStudent(student, askQ))}
            placeholder="e.g. father's college, birth date, passport number, languages"
            style={{ flex: "1 1 260px", fontSize: 13, padding: "9px 12px",
              border: `1px solid ${BORDER}`, borderRadius: 8 }} />
          <button onClick={() => setAskRes(askStudent(student, askQ))}
            style={{ background: Y, border: "none", borderRadius: 8, padding: "9px 18px",
              fontSize: 13, fontWeight: 700, color: YD, cursor: "pointer" }}>Ask</button>
        </div>
        {askRes !== null && (
          askRes.length === 0 ? (
            <div style={{ fontSize: 13, color: SUB, marginTop: 12 }}>
              Nothing matching in the onboarding responses.
            </div>
          ) : (
            <div style={{ marginTop: 12, display: "grid", gap: "10px 18px",
              gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))" }}>
              {askRes.map(r => (
                <div key={r.key}>
                  <div style={{ fontSize: 11, color: SUB, fontWeight: 600 }}>{r.label}</div>
                  <div style={{ fontSize: 13.5, color: INK, fontWeight: 700 }}>{r.value}</div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* live status — Ravisha's at-a-glance panel */}
      <div style={sectionTitle}>Live status</div>
      <LiveStatus student={student} onbLive={onbLive} />

      {/* onboarding link */}
      <div style={sectionTitle}>Onboarding link</div>
      <div style={card}>
        <div style={{ fontSize: 13, color: SUB, marginBottom: 8 }}>
          Send this personal link to the student to complete onboarding.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input readOnly value={onbUrl} style={{ flex: "1 1 280px", fontSize: 12.5,
            padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 8, color: MID }} />
          <button onClick={() => copy(onbUrl)} style={{ background: Y, border: "none",
            borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, color: YD,
            cursor: "pointer" }}>{copied ? "Copied" : "Copy link"}</button>
          <button onClick={sendOnb} disabled={sendingOnb} style={{ background: "#25D366",
            border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
            color: "#fff", cursor: "pointer", opacity: sendingOnb ? .6 : 1 }}>
            {sendingOnb ? "Sending…" : student.onbEmailSentAt ? "Resend ONB email" : "Send ONB email"}
          </button>
        </div>
      </div>

      <div style={sectionTitle}>Essay submission link</div>
      <div style={card}>
        <div style={{ fontSize: 13, color: SUB, marginBottom: 8 }}>
          Send this to the student just to submit essays — opens straight to the essay form,
          not the full portal.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input readOnly value={essayUrl} style={{ flex: "1 1 280px", fontSize: 12.5,
            padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 8, color: MID }} />
          <button onClick={() => {
            navigator.clipboard.writeText(essayUrl);
            setCopiedEssay(true); setTimeout(() => setCopiedEssay(false), 1500);
          }} style={{ background: Y, border: "none", borderRadius: 8, padding: "9px 16px",
            fontSize: 13, fontWeight: 700, color: YD, cursor: "pointer" }}>
            {copiedEssay ? "Copied" : "Copy link"}</button>
        </div>
      </div>

      {/* legacy google-sheet portal */}
      {student.portalLink && (
        <>
          <div style={sectionTitle}>Legacy TUAP sheet</div>
          <div style={card}>
            <div style={{ fontSize: 13, color: SUB, marginBottom: 8 }}>
              Their existing Google-Sheet portal — used as the source of truth before we migrate the
              data into this dashboard.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input readOnly value={student.portalLink} style={{ flex: "1 1 280px", fontSize: 12.5,
                padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 8, color: MID }} />
              <a href={student.portalLink} target="_blank" rel="noreferrer"
                style={{ background: "#1A73E8", color: "#fff", borderRadius: 8, padding: "9px 16px",
                  fontSize: 13, fontWeight: 700, textDecoration: "none" }}>Open sheet</a>
            </div>
          </div>
        </>
      )}

      {/* whatsapp group */}
      <div style={sectionTitle}>WhatsApp group</div>
      <div style={card}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: INK }}>{groupName}</div>
        {student.whatsappLink ? (
          <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input readOnly value={student.whatsappLink} style={{ flex: "1 1 280px", fontSize: 12.5,
              padding: "9px 11px", border: `1px solid ${BORDER}`, borderRadius: 8, color: MID }} />
            <a href={student.whatsappLink} target="_blank" rel="noreferrer"
              style={{ background: "#25D366", color: "#fff", borderRadius: 8, padding: "9px 16px",
                fontSize: 13, fontWeight: 700, textDecoration: "none" }}>Open group</a>
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12.5, color: student.whatsappGroupId ? YD : SUB, marginBottom: 10 }}>
              {student.whatsappGroupId
                ? "The group EXISTS on the bot's phone, but its invite link was never saved, so nobody can be sent in. Open the group on the phone, copy the invite link and paste it here. Do not create another group."
                : student.whatsappRequestedAt
                  ? "Group creation requested — the bot will create it and add the link here shortly."
                  : "No group yet. Create it automatically, or paste an existing invite link."}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {/* Hidden once a group id exists: pressing it again would make a
                  SECOND group for the same student. */}
              {!student.whatsappGroupId && (
                <>
                  <button onClick={() => requestTuapGroup(student).catch(() => {})}
                    style={{ background: "#25D366", color: "#fff", border: "none", borderRadius: 8,
                      padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    Create group automatically
                  </button>
                  <span style={{ fontSize: 12, color: SUB }}>or</span>
                </>
              )}
              <input value={link} onChange={e => setLink(e.target.value)}
                placeholder="Paste chat.whatsapp.com/... link"
                style={{ flex: "1 1 220px", fontSize: 12.5, padding: "9px 11px",
                  border: `1px solid ${BORDER}`, borderRadius: 8 }} />
              <button onClick={() => updateTuapStudent(student.id, { whatsappLink: link.trim() })}
                disabled={!link.trim()}
                style={{ background: Y, border: "none", borderRadius: 8, padding: "9px 14px",
                  fontSize: 13, fontWeight: 700, color: YD, cursor: "pointer",
                  opacity: link.trim() ? 1 : .5 }}>Save</button>
            </div>
          </div>
        )}
      </div>

      {/* stage — auto-synced from events, not editable */}
      <div style={sectionTitle}>Stage <span style={{ fontSize: 11, fontWeight: 600,
        color: SUB, textTransform: "none", letterSpacing: 0 }}>· updates automatically</span></div>
      <div style={{ ...card, display: "flex", gap: 7, flexWrap: "wrap" }}>
        {TUAP_PIPELINE.map((s, i) => {
          const cur = TUAP_PIPELINE.indexOf(student.status);
          const [fg, bg] = i < cur ? ["#16a34a", "#dcfce7"]
            : i === cur ? ["#a16207", "#fef9c3"] : ["#9aa0a6", "#f1f1f3"];
          return (
            <span key={s} style={{ fontSize: 12, fontWeight: 800, padding: "7px 12px",
              borderRadius: 999, border: `1.5px solid ${fg}`, background: bg, color: fg }}>
              {s}</span>
          );
        })}
      </div>

      {/* full student portal — team-editable */}
      <div style={sectionTitle}>
        Student portal
        {onbLive.done || student.onbComplete
          ? <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 700 }}> · onboarding submitted</span>
          : onbLive.started
          ? <span style={{ fontSize: 12, color: MID, fontWeight: 700 }}> · onboarding {onbLive.pct}% filled (live)</span>
          : <span style={{ fontSize: 12, color: SUB, fontWeight: 600 }}> · onboarding not started yet</span>}
      </div>
      <StudentPortal student={student} role="team" />

      {/* files modal */}
      {showFiles && (
        <div onClick={() => setShowFiles(false)} style={{ position: "fixed", inset: 0,
          zIndex: 200, background: "rgba(15,15,20,.6)", display: "flex",
          alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14,
            width: "100%", maxWidth: 560, maxHeight: "88vh", overflow: "auto", padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 12 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: INK }}>Uploaded files</span>
              <button onClick={() => setShowFiles(false)} style={{ background: "#f1f1f4",
                border: "none", borderRadius: 8, padding: "7px 13px", fontSize: 13,
                fontWeight: 800, color: SUB, cursor: "pointer" }}>Close</button>
            </div>
            {Object.keys(allFiles).length === 0 ? (
              <div style={{ fontSize: 13, color: SUB, padding: "20px 0" }}>No files uploaded yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {Object.keys(allFiles).map(k => {
                  const f = allFiles[k];
                  const doc = ONB_DOCUMENTS.find(d => d.key === k);
                  const name = (doc && doc.label) || f.name || k;
                  return (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 10,
                      border: `1px solid ${BORDER}`, borderRadius: 9, padding: "9px 12px" }}>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: INK }}>{name}</span>
                      <button onClick={() => setLb({ ...f, name })} style={{ background: Y,
                        border: "none", borderRadius: 7, padding: "6px 12px", fontSize: 12,
                        fontWeight: 800, color: YD, cursor: "pointer" }}>View</button>
                      <a href={f.data} download={f.name || name} style={{ background: "#f1f1f4",
                        borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 800,
                        color: SUB, textDecoration: "none" }}>Download</a>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      <Lightbox file={lb} onClose={() => setLb(null)} />
    </div>
  );
}

/* ── Main ── */
/* ── OTG Essays audit — safety net for the name match ── */
function OtgEssaysAudit({ students, onBack }) {
  const [essays, setEssays] = useState(null);
  useEffect(() => { fetchOtgEssays().then(setEssays).catch(() => setEssays([])); }, []);

  const wrap = { maxWidth: 1100, margin: "0 auto", padding: "22px 26px 60px" };
  if (essays === null) {
    return <div style={wrap}><div style={{ color: SUB, fontSize: 14 }}>Loading OTG essays…</div></div>;
  }
  const rows = essays.map(e => ({ e, matches: students.filter(s => essayMatchesStudent(e, s)) }));
  const matched = rows.filter(r => r.matches.length === 1);
  const multi = rows.filter(r => r.matches.length > 1);
  const unmatched = rows.filter(r => r.matches.length === 0);
  unmatched.forEach(r => console.warn("OTG essay unmatched —", essayNames(r.e), "essayId:", r.e.id));

  const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 10,
    padding: "12px 15px", marginBottom: 8 };
  const head = { fontFamily: "'Space Mono',monospace", fontSize: 12, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: ".06em", margin: "26px 0 10px" };
  const Row = ({ r, tone }) => {
    const n = essayNames(r.e);
    return (
      <div style={card}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: INK }}>
            {(n.first + " " + n.last).trim() || r.e.studentName || "Unnamed"}</span>
          <span style={{ fontSize: 12.5, color: SUB }}>
            {[r.e.college, r.e.type].filter(Boolean).join(" · ")}</span>
          <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: tone }}>
            {r.matches.length === 1 ? "→ " + r.matches[0].studentName
              : r.matches.length > 1 ? r.matches.length + " students match — review spelling"
              : "No TUAP student matched"}</span>
        </div>
      </div>
    );
  };
  return (
    <div style={wrap}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: SUB,
        fontSize: 13, fontWeight: 700, cursor: "pointer", padding: 0, marginBottom: 14 }}>
        ← Back to TUAP students</button>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: INK }}>OTG Essays</h1>
      <p style={{ margin: "3px 0 0", fontSize: 13.5, color: SUB }}>
        {essays.length} essays from the OTG essay tool, auto-matched to TUAP students by name.
        A student's portal shows only their own matched essays.</p>

      {multi.length > 0 && (
        <>
          <div style={{ ...head, color: "#b45309" }}>Needs review — {multi.length}</div>
          {multi.map((r, i) => <Row key={i} r={r} tone="#b45309" />)}
        </>
      )}
      <div style={{ ...head, color: "#b91c1c" }}>Unmatched — {unmatched.length}</div>
      {unmatched.length === 0
        ? <div style={{ fontSize: 13, color: SUB }}>None — every essay matched a student.</div>
        : unmatched.map((r, i) => <Row key={i} r={r} tone="#b91c1c" />)}
      <div style={{ ...head, color: "#15803d" }}>Matched — {matched.length}</div>
      {matched.length === 0
        ? <div style={{ fontSize: 13, color: SUB }}>None yet.</div>
        : matched.map((r, i) => <Row key={i} r={r} tone="#15803d" />)}
    </div>
  );
}

export default function TuapAdmin({ onBack }) {
  const [students, setStudents] = useState([]);
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [audit, setAudit] = useState(false);
  const [hub, setHub] = useState(false);
  // Live NEW-onboarding progress (onboarding.neerajmandhana.com -> otg-2026
  // onboardingProfiles). Replaces the stale local onbAnswers copy.
  const [onbProfiles, setOnbProfiles] = useState([]);

  useEffect(() => subscribeTuapStudents(setStudents, () => {}), []);
  useEffect(() => subscribeOnboardingProfiles(setOnbProfiles), []);

  const selected = students.find(s => s.id === sel);

  if (audit) {
    return (
      <div style={{ minHeight: "100vh", background: BG, fontFamily: FONT }}>
        <style>{FONTS}</style>
        <OtgEssaysAudit students={students} onBack={() => setAudit(false)} />
      </div>
    );
  }
  if (hub) {
    return (
      <div style={{ minHeight: "100vh", background: BG, fontFamily: FONT }}>
        <style>{FONTS}</style>
        <RavishaHub students={students} onBack={() => setHub(false)} />
      </div>
    );
  }
  const shown = q
    ? students.filter(s => (s.studentName + " " + s.school + " " + s.grade)
        .toLowerCase().includes(q.toLowerCase()))
    : students;

  if (selected) {
    return (
      <div style={{ minHeight: "100vh", background: BG, fontFamily: FONT }}>
        <style>{FONTS}</style>
        <Detail student={selected} onBack={() => setSel(null)} onbProfiles={onbProfiles} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: FONT }}>
      <style>{FONTS}</style>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "22px 26px 60px" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: SUB,
          fontSize: 13, fontWeight: 700, cursor: "pointer", padding: 0, marginBottom: 14 }}>
          ← Back to FME dashboard
        </button>

        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between",
          gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: INK }}>TUAP Students</h1>
            <p style={{ margin: "3px 0 0", fontSize: 13.5, color: SUB }}>
              {students.length} student{students.length === 1 ? "" : "s"} converted from FM.
              Onboarding, responses and WhatsApp groups in one place.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search students"
              style={{ fontSize: 13, padding: "9px 13px", border: `1px solid ${BORDER}`,
                borderRadius: 9, minWidth: 180 }} />
            <button onClick={() => setHub(true)} style={{ background: "#25D366",
              color: "#fff", border: "none", borderRadius: 9, padding: "9px 16px",
              fontSize: 13, fontWeight: 800, cursor: "pointer" }}>Communications</button>
            <button onClick={() => setAudit(true)} style={{ background: "#eef2ff",
              border: "none", borderRadius: 9, padding: "9px 16px", fontSize: 13,
              fontWeight: 800, color: "#3730a3", cursor: "pointer" }}>OTG Essays</button>
          </div>
        </div>

        {students.length === 0 ? (
          <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12,
            padding: "44px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>No TUAP students yet</div>
            <div style={{ fontSize: 13, color: SUB, marginTop: 5 }}>
              Use the "Convert to TUAP" button on an FM lead to bring a student here.
            </div>
          </div>
        ) : (
          <StudentGroupedList
            shown={shown} q={q} onOpen={setSel} onCopied={setCopiedId}
            copiedId={copiedId} onbProfiles={onbProfiles}
          />
        )}
      </div>
    </div>
  );
}

// One table per grade. Heading shows the grade + count. Search collapses
// everything into a single table because the user is looking for one student.
function StudentGroupedList({ shown, q, onOpen, onCopied, copiedId, onbProfiles }) {
  const [collapsed, setCollapsed] = useState({});
  function toggle(g) { setCollapsed(c => ({ ...c, [g]: !c[g] })); }

  async function del(s) {
    if (!window.confirm(`Delete ${s.studentName || "this student"}?\n\nThis only removes the student record. Their WhatsApp group on Ravisha's phone is untouched.`)) return;
    try { await deleteTuapStudent(s.id); }
    catch (e) { alert("Delete failed: " + (e.message || "unknown")); }
  }

  const grouped = q
    ? [{ grade: `Search · "${q}"`, students: shown }]
    : groupByGrade(shown);

  function Row({ s }) {
    // LIVE new-onboarding status (from otg-2026 onboardingProfiles), not the
    // stale local onbAnswers. started=false => they have not opened onboarding.
    const live = onbLiveForStudent(onbProfiles, s);
    const emailSent = !!s.onbEmailSentAt || /ONB|Onboard|Profile|Active/i.test(s.status || "");
    const pct = live.started ? live.pct : (s.onbComplete ? 100 : 0);
    const onbLabel = live.done ? "Complete"
      : live.started ? `In progress · ${pct}%`
      : emailSent ? "Email sent · not started"
      : "Not sent";
    const barColor = live.done ? "#16a34a" : live.started ? Y : "#d1d5db";
    const ls = liveStatus(s);
    return (
      <tr key={s.id} style={{ borderTop: `1px solid ${BORDER}` }}>
        <td style={{ padding: "11px 12px", fontWeight: 700, color: INK }}>
          {s.studentName || "Unnamed"}
          {s.track && (
            <div style={{ fontSize: 11, color: SUB, fontWeight: 500 }}>{s.track}</div>
          )}
        </td>
        <td style={{ padding: "11px 12px", color: MID }}>{s.school || "—"}</td>
        <td style={{ padding: "11px 12px", color: MID }}>{s.city || "—"}</td>
        <td style={{ padding: "11px 12px" }}><Badge text={s.status} /></td>
        <td style={{ padding: "11px 12px", minWidth: 150 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ flex: 1, height: 7, background: "#eee", borderRadius: 999 }}>
              <div style={{ width: `${pct}%`, height: "100%", background: barColor,
                borderRadius: 999, transition: "width .3s" }} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: SUB }}>{pct}%</span>
          </div>
          <div style={{ fontSize: 10.5, fontWeight: 700, marginTop: 3,
            color: live.done ? "#16a34a" : live.started ? MID : SUB }}>{onbLabel}</div>
        </td>
        <td style={{ padding: "11px 12px" }}>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {ls.groupCreated ? (
              <>
                <span title={ls.membersKnown
                  ? `${ls.joinedCount} of ${ls.expected || 3} joined`
                  : "The group exists. The bot has not reported who is in it yet."}
                  style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 8px",
                    borderRadius: 999, color: "#16a34a", background: "#dcfce7" }}>
                  {ls.membersKnown ? `Group · ${ls.joinedCount}/${ls.expected || 3}` : "Group live"}
                </span>
                {!ls.linkReady && (
                  <span title="The group was created but the invite link was never saved, so nobody can be sent in."
                    style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 8px",
                      borderRadius: 999, color: YD, background: "#FEF3C7" }}>
                    No link
                  </span>
                )}
              </>
            ) : (
              <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 8px",
                borderRadius: 999, color: SUB, background: "#f0f0f4" }}>
                No group
              </span>
            )}
            {ls.welcomeSentAt && (
              <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 8px",
                borderRadius: 999, color: "#16a34a", background: "#dcfce7" }}>
                Welcome
              </span>
            )}
            {ls.profileMeetingAt && (
              <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 8px",
                borderRadius: 999, color: "#16a34a", background: "#dcfce7" }}>
                Meeting
              </span>
            )}
          </div>
        </td>
        <td style={{ padding: "11px 12px", whiteSpace: "nowrap" }}>
          <button onClick={() => onOpen(s.id)} style={{ background: Y, border: "none",
            borderRadius: 7, padding: "6px 13px", fontSize: 12, fontWeight: 700,
            color: YD, cursor: "pointer", marginRight: 6 }}>Open</button>
          <button onClick={() => {
            navigator.clipboard.writeText(portalUrl(s));
            onCopied(s.id); setTimeout(() => onCopied(null), 1400);
          }} style={{ background: "#f1f1f4", border: "none", borderRadius: 7,
            padding: "6px 12px", fontSize: 12, fontWeight: 700, color: SUB,
            cursor: "pointer", marginRight: 6 }}>
            {copiedId === s.id ? "Copied" : "Portal link"}
          </button>
          <button onClick={() => del(s)} title="Delete this student"
            style={{ background: "#FEE2E2", border: "none", borderRadius: 7,
              padding: "6px 10px", fontSize: 12, fontWeight: 700, color: "#B91C1C",
              cursor: "pointer" }}>Delete</button>
        </td>
      </tr>
    );
  }

  const Th = ({ children }) => (
    <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 11,
      fontWeight: 800, color: SUB, textTransform: "uppercase",
      letterSpacing: ".04em" }}>{children}</th>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {grouped.map(({ grade, students }) => {
        const open = !collapsed[grade];
        return (
          <div key={grade} style={{ background: CARD, border: `1px solid ${BORDER}`,
            borderRadius: 12, overflow: "hidden" }}>
            <button onClick={() => toggle(grade)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", background: "#fafafa",
                padding: "12px 16px", border: "none", cursor: "pointer",
                borderBottom: open ? `1px solid ${BORDER}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                  style={{ transform: open ? "rotate(90deg)" : "none",
                    transition: "transform .15s" }}>
                  <path d="M9 6l6 6-6 6" stroke={SUB} strokeWidth="3"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontSize: 14, fontWeight: 900, color: INK }}>{grade}</span>
                <span style={{ fontSize: 11, fontWeight: 800, color: YD,
                  background: "#FEF3C7", padding: "3px 9px", borderRadius: 999 }}>
                  {students.length}
                </span>
              </div>
            </button>
            {open && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#fff" }}>
                      <Th>Name</Th><Th>School</Th><Th>City</Th><Th>Stage</Th>
                      <Th>Onboarding</Th><Th>Live status</Th><Th>{""}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(s => <Row key={s.id} s={s} />)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

